import type { ReactNode } from 'react';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTarget from '@atlaskit/onboarding/spotlight-target';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { TopLevelSpacer } from '@atlaskit/side-nav-items/top-level-spacer';

import { ConfluenceWorkspaceAri, IdentityUserAri } from '@atlassian/ari';
import type {
	GlobalShortcutLinkItem,
	fnGetFilteredProductLinks,
} from '@atlassian/global-shortcuts/types';
import { useJira } from '@atlassian/global-shortcuts/jira';
import { InvitedAppShortcutsContainer } from '@atlassian/cross-join-nudges/invited-app-shortcuts/invited-app-shortcuts-container/main';
import { useInvitedAppProductLinks } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-product-links';
import { ShortcutsMenu } from '@atlassian/cross-join-nudges/invited-app-shortcuts/shortcuts-menu/main';
import type { ShortcutItemsConfig } from '@atlassian/cross-join-nudges/types';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/common/types';

import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	useRouteActions,
	useRouteDataRef,
	useTransitionId,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { LoadableLazy } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useFlyoutActions } from '@confluence/flyout-store';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { SimplifyNavNewUserSpotlight } from '@confluence/experiment-simplify-nav-new-user/entry-points/SimplifyNavNewUserSpotlight.ts';
import { useSimplifiedNavSpotlight } from '@confluence/experiment-simplify-nav-new-user/entry-points/useSimplifiedNavSpotlight.ts';
import { useSimplifyNavNewUserSpotlightStoreActions } from '@confluence/experiment-simplify-nav-new-user/entry-points/useSimplifyNavNewUserSpotlightStore.ts';
import { ConnectorPromoSidebarEntry } from '@confluence/experiment-twcg-1489-cc-3p-connector-onboarding/entry-points/ConnectorPromoSidebarEntry.ts';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';

import { CompanyHubItem } from '../PlatformNavigation/AppShortcuts/apps/CompanyHubItem/CompanyHubItem.tsx';
import { useResolveInvitedAppShortcuts } from '../PlatformNavigation/AppShortcuts/hooks/useResolveInvitedAppShortcuts.tsx';
import type { MenuId } from '../PlatformNavigation/PlatformNavigationTypes.ts';

import { usePlatformCustomizationsWithSimplifyNav } from './usePlatformCustomizationsWithSimplifyNav.ts';
import { GLOBAL_NAVIGATION_METRIC } from './perf.config.ts';
import { useMenuItems } from './useMenuItems.ts';
import { MoreMenuItem } from './MoreMenuItem.tsx';
import { GlobalAppShortcutsSection } from './GlobalAppShortcuts/GlobalAppShortcutsSection.tsx';
import { CustomHomeItem } from './GlobalItems/CustomHomeItem.tsx';
import { ForYouItem } from './GlobalItems/ForYouItem.tsx';
import { RecentItem } from './GlobalItems/RecentItem.tsx';
import { StarredItem } from './GlobalItems/StarredItem.tsx';
import { SpacesItem } from './GlobalItems/SpacesItem.tsx';
import { AppsItem } from './GlobalItems/AppsItem/index.tsx';
import { TopicsItem } from './GlobalItems/TopicsItem.tsx';
import { GoalsItem } from './GlobalItems/GoalsItem.tsx';
import { TeamsItem } from './GlobalItems/TeamsItem.tsx';
import { DraftsItem } from './GlobalItems/DraftsItem.tsx';
import { TasksItem } from './GlobalItems/TasksItem.tsx';
import { TemplatesItem } from './GlobalItems/TemplatesItem.tsx';
import { CustomizeSidebarItem } from './GlobalItems/CustomizeSidebarItem.tsx';
import type {
	GlobalNavigationComponentQuery$data,
	GlobalNavigationComponentQuery$variables,
} from './__generated__/GlobalNavigationComponentQuery.graphql.ts';
import { ReorderShortcutsContainer } from './reorderShortcutsContainer.tsx';
import { useLegacyInvitedAppShortcutsRecommendations } from './LegacyInvitedAppShortcutsRecommendations.tsx';

type SettingsMenuItem = NonNullable<
	NonNullable<
		NonNullable<GlobalNavigationComponentQuery$data['settings_navigationCustomisation']>['sidebar']
	>['nodes']
>[number];

const CustomizationModalLoader = LoadableLazy({
	loader: async () => {
		const { CustomizationModalComponent } = await import(
			/* webpackChunkName: "loadable-CustomizationModal" */ './CustomizeSidebar/CustomizationModal.tsx'
		);
		return CustomizationModalComponent;
	},
});

export const PLATFORM_MENU_ID_PREFIX = 'confluence.sidebar.';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireMoreMenuClickedAnalytics,
);

const hasAccess = (accessStatus: AccessStatus | null) =>
	![AccessStatus.ANONYMOUS_ACCESS, AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS, null].includes(
		accessStatus,
	);

const hasGlobalAppShortcutsAccess = (accessStatus: AccessStatus | null) =>
	![
		AccessStatus.ANONYMOUS_ACCESS,
		AccessStatus.EXTERNAL_SHARE_ACCESS,
		AccessStatus.EXTERNAL_COLLABORATOR_ACCESS,
		AccessStatus.NOT_PERMITTED,
		null,
	].includes(accessStatus);

type GlobalNavigationComponentProps = {
	productSideNavigation?: ReactNode;
	isOnAnySettingsScreen?: boolean;
	isCollapsed?: boolean;
};

const GlobalItemComponents = {
	customHome: CustomHomeItem,
	yourWork: ForYouItem,
	companyHub: CompanyHubItem,
	recent: RecentItem,
	starred: StarredItem,
	spaces: SpacesItem,
	apps: AppsItem,
	goals: GoalsItem,
	topics: TopicsItem,
	teams: TeamsItem,
	drafts: DraftsItem,
	tasks: TasksItem,
	templates: TemplatesItem,
	customize: CustomizeSidebarItem,
};

export const GlobalNavigationComponent = ({
	productSideNavigation,
	isOnAnySettingsScreen,
	isCollapsed,
}: GlobalNavigationComponentProps): React.JSX.Element => {
	const { accessStatus, cloudId, userId, activationId } = useSessionData();
	const [peekingId, setPeekingId] = useState<MenuId | undefined>(undefined);
	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState<boolean>(false);
	const routeDataRef = useRouteDataRef();
	const { push } = useRouteActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);
	const { openFlyout, closeFlyout } = useFlyoutActions();
	const transitionId = useTransitionId();
	const [getFilteredProductLinks, setGetFilteredProductLinks] = useState<
		fnGetFilteredProductLinks | undefined
	>(undefined);

	const [isCustomizeSidebarDialogVisible, setIsCustomizeSidebarDialogVisible] =
		useState<boolean>(false);

	const jiraShortcut: GlobalShortcutLinkItem = useJira('');
	const invitedAppShortcutsExperiment = useResolveInvitedAppShortcuts();
	const isEligibleToSeeInvitedAppShortcuts = useMemo(
		() =>
			// we split the kill-switch gate and targeting gate to avoid the overrides precedence issue
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('twcg-460-personalised-invite-notifications-m2-gate') &&
			invitedAppShortcutsExperiment.ineligibilityReason === null &&
			invitedAppShortcutsExperiment.invitedApps?.jira?.eligible === true,
		[invitedAppShortcutsExperiment],
	);

	useEffect(() => {
		if (transitionId && transitionId > 0) {
			//on any transition close open flyout. On initial Load transitionId = 0
			closeFlyout();
		}
	}, [closeFlyout, transitionId]);

	const { setHasOpenedCustomize } = useSimplifyNavNewUserSpotlightStoreActions();

	const { data, error } = useQueryInterop<
		GlobalNavigationComponentQuery$data,
		GlobalNavigationComponentQuery$variables
	>(GlobalNavigationComponentQuery, {
		interopApolloAggClient: true,
		variables: {
			entityAri: ConfluenceWorkspaceAri.create({ siteId: cloudId, activationId }).toString(),
			ownerAri: userId ? IdentityUserAri.create({ userId }).toString() : '',
		},
		skip: !userId || !activationId,
	});

	const platformCustomizations = useMemo(() => {
		if (data?.settings_navigationCustomisation?.sidebar?.nodes) {
			return data.settings_navigationCustomisation.sidebar.nodes
				.filter((node): node is NonNullable<typeof node> => node != null)
				.map((node) => ({
					menuId: node.menuId?.replace(PLATFORM_MENU_ID_PREFIX, '') as MenuId,
					visible: Boolean(node.visible),
				}));
		}
		return [];
	}, [data]);

	const { platformCustomizationsWithSimplifyNav, navStyle, isWithinD0, source } =
		usePlatformCustomizationsWithSimplifyNav(platformCustomizations);

	//only show customization and fetch data if we meet these conditions
	const showCustomization = !!activationId && hasAccess(accessStatus);

	// only render global app shortcuts if we meet these conditions
	const showGlobalAppShortcuts = hasGlobalAppShortcutsAccess(accessStatus);

	// Productionisation experiment (PUMA-2012). Two separate flags to keep exposure balanced:
	//
	// * `isProductionisationEligible` — the kill-switch gate + prerequisites (cloudId/userId/GAS
	//   access). This is the whole cohort: BOTH experiment arms. It drives the GRS read + exposure
	//   (below) so treatment and control are exposed at the same divergence point ("has a rec").
	// * `isProductionisationTreatment` — eligible AND the experiment resolves to the treatment arm.
	//   Read with `UNSAFE_expValEqualsNoExposure` (no exposure — the real exposure is fired inside the
	//   hook, tagged by `expVal`). This drives only the RENDER (show the decorated GRS nudge + hand the
	//   Jira shortcut over from GlobalAppShortcuts); control falls through to the experiment/control
	//   path. Takes precedence over the m2 experiment cohort.
	const isProductionisationEligible =
		showGlobalAppShortcuts &&
		!!cloudId &&
		!!userId &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('twcg-460-preload-grs-invited-app-shortcuts-gate');
	const isProductionisationTreatment =
		isProductionisationEligible &&
		UNSAFE_expValEqualsNoExposure('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', true);

	// Read the GRS recommendations for the WHOLE gated cohort — both experiment arms — not just the
	// treatment arm. This keeps the exposure balanced: the hook fires the `twcg-460-preload-grs-…`
	// exposure for treatment and control alike (tagged by `expVal`) whenever there is a recommendation
	// to show, so both arms enter the analysis. Only the render (below) is treatment-gated. Gating the
	// read on `isProductionisationTreatment` instead would exclude control from exposure entirely.
	// `recommendedApps` also drives whether the decorated nudge renders and whether the Jira shortcut
	// is handed over from the main GlobalAppShortcuts section — an empty/cache-miss result keeps Jira
	// in GlobalAppShortcuts instead of hiding it.
	const {
		recommendedApps: productionisationRecommendedApps,
		resolverResult: productionisationResolverResult,
	} = useLegacyInvitedAppShortcutsRecommendations({
		cloudId: cloudId ?? '',
		userId: userId ?? '',
		enabled: isProductionisationEligible,
	});
	const isProductionisationTreatmentActive =
		isProductionisationTreatment && productionisationRecommendedApps.length > 0;

	const { globalMenuItems, moreMenuItems, appShortcutMenuItems, allMenuItems } = useMenuItems(
		showCustomization,
		accessStatus,
		platformCustomizationsWithSimplifyNav,
		getFilteredProductLinks,
	);

	const { shouldShowSpotlight, shouldPersistCustomizeOpen } = useSimplifiedNavSpotlight({
		globalMenuItems,
		navStyle,
		isWithinD0,
	});

	const moreMenuOnClickCallback = useCallback(
		(menuId: MenuId) => {
			setIsMoreMenuOpen(!isMoreMenuOpen);

			// TODO: it would be great if we could refactor the following menu-specific logic into the respective menu components
			if (menuId === 'customize') {
				setIsCustomizeSidebarDialogVisible(true);
				// Persist for treatment users with Recent in main nav (includes D0) — so D0 users who open customize don't see spotlight when they become D1+
				if (shouldPersistCustomizeOpen) {
					setHasOpenedCustomize();
				}
				void fireClickAnalytics('customiseSidebar');
			} else {
				void fireClickAnalytics(menuId);
			}

			if (menuId === 'recent' || menuId === 'starred' || menuId === 'spaces') {
				openFlyout(menuId);
				setPeekingId(menuId);
			}

			if (menuId === 'apps') {
				push('/wiki/marketplace/discover');
			}
		},
		[
			fireClickAnalytics,
			isMoreMenuOpen,
			openFlyout,
			push,
			setHasOpenedCustomize,
			shouldPersistCustomizeOpen,
		],
	);

	const visibleMenuIds = useMemo(() => {
		return appShortcutMenuItems.reduce<string[]>(
			(acc, item) => (item.visible ? [...acc, item.menuId] : acc),
			[],
		);
	}, [appShortcutMenuItems]);

	const isInvitedAppShortcutsEnabled = useMemo(
		() =>
			isEligibleToSeeInvitedAppShortcuts &&
			expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false),
		[isEligibleToSeeInvitedAppShortcuts],
	);

	// The invited-app treatment "takes over" the Jira shortcut — GlobalAppShortcuts strips it (see
	// GlobalAppShortcutsWrapper) — only when it is actively rendering the decorated nudge: the m2
	// experiment cohort when enabled, or the productionisation cohort when GRS actually recommends an
	// app. On an empty / cache-miss GRS result the productionisation treatment renders nothing, so
	// Jira must stay in GlobalAppShortcuts (PUMA-2012). Productionisation takes precedence over m2.
	const shouldHandOverJiraShortcut = isProductionisationTreatment
		? isProductionisationTreatmentActive
		: isInvitedAppShortcutsEnabled;

	const invitedAppShortcutsConfig: ShortcutItemsConfig = useMemo(() => {
		// Configure the invited (Jira) shortcut for the experiment cohort OR the productionisation
		// cohort (PUMA-2012). In productionisation mode the container gates actual visibility on the
		// GRS recommendations; the config only needs to carry the shortcut so its product links resolve.
		if (!isInvitedAppShortcutsEnabled && !isProductionisationTreatment) {
			return {};
		}
		return {
			jira: jiraShortcut,
		};
	}, [isInvitedAppShortcutsEnabled, isProductionisationTreatment, jiraShortcut]);

	const { invitedAppProductLinks, handleUpdateInvitedAppProductLinks } = useInvitedAppProductLinks({
		shortcutItemsConfig: invitedAppShortcutsConfig,
		getCachedProductLinks: invitedAppShortcutsExperiment.getCachedProductLinks,
		updateHydratedProductLinks: invitedAppShortcutsExperiment.updateHydratedProductLinks,
	});

	useEffect(() => {
		if (getFilteredProductLinks && fg('twcg-460-personalised-invite-notifications-m2-gate')) {
			invitedAppShortcutsExperiment.updateFromAppAccessFilter({
				getFilteredProductLinks,
				limitToOnlyApsKeys: ['JIRA_SOFTWARE'],
				visibleMenuIds,
			});
		}
	}, [
		appShortcutMenuItems,
		getFilteredProductLinks,
		invitedAppShortcutsExperiment,
		visibleMenuIds,
	]);

	// The item "Customize sidebar" when it's one of the visible global items and app shortcuts:
	let globalCustomizeMenuItem: ReactNode = null;

	const moreMenuItem = (
		<MoreMenuItem
			moreMenuItems={moreMenuItems}
			isOpen={isMoreMenuOpen}
			onClick={moreMenuOnClickCallback}
			toggleMenu={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
			peekingId={peekingId}
			isCollapsed={isCollapsed}
		/>
	);

	useEffect(() => {
		// Don't fire the m2 experiment exposure when the productionisation cohort is active: that user
		// is shown the productionisation treatment (whose exposure is logged inside
		// `InvitedAppShortcutsRecommendations`), so firing here would mis-attribute them to the m2
		// cohort (PUMA-2012).
		if (isEligibleToSeeInvitedAppShortcuts && !isProductionisationTreatment) {
			invitedAppShortcutsExperiment.fireFeatureExposed({
				isEnabled: expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false),
			});
		}
	}, [
		isEligibleToSeeInvitedAppShortcuts,
		isProductionisationTreatment,
		invitedAppShortcutsExperiment,
	]);

	// Shared leaf renderer for the invited-app-shortcuts menu, used by both the experiment cohort and
	// the productionisation cohort (PUMA-2012): order the decorated shortcuts per the user's
	// customisation, then render them with the resolved product links. Keeping one renderer means the
	// two cohorts differ only in how eligibility is sourced, not in how the menu looks.
	const renderInvitedAppShortcutsMenu = (referrableShortcuts: ShortcutsConfig) => (
		<ReorderShortcutsContainer
			shortcutsConfig={referrableShortcuts}
			customizationMenuConfig={platformCustomizationsWithSimplifyNav}
		>
			{({ shortcuts }) => (
				<ShortcutsMenu
					shortcutItems={shortcuts}
					product="confluence"
					invitedAppProductLinks={invitedAppProductLinks}
				/>
			)}
		</ReorderShortcutsContainer>
	);

	return (
		<>
			<MenuList>
				{!isOnAnySettingsScreen && (
					<SpotlightTarget name="enhance-orientation-spotlight-tour-target-3">
						<div data-vc="nav4-globalItems">
							{globalMenuItems.map(({ menuId }) => {
								const Component = GlobalItemComponents[menuId];
								let node: ReactNode = null;
								if (Component) {
									const baseNode = (
										<Component
											key={menuId}
											setIsCustomizeSidebarDialogVisible={setIsCustomizeSidebarDialogVisible}
										/>
									);
									// Spotlight anchored to Recent when in main nav (ACTIVATION-2059).
									// Always render the wrapper and pass `shouldShowSpotlight`; when not eligible,
									// the wrapper returns only the Recent item with no spotlight UI.
									node =
										menuId === 'recent' ? (
											<SimplifyNavNewUserSpotlight
												key={menuId}
												shouldShowSpotlight={shouldShowSpotlight}
												source={source}
											>
												{baseNode}
											</SimplifyNavNewUserSpotlight>
										) : (
											baseNode
										);
									if (menuId === 'companyHub') {
										// companyHub is always rendered via GlobalAppShortcutsSection —
										// suppress it here to prevent a double-render.
										return null;
									} else if (menuId === 'customize') {
										// The item "Customize sidebar" should be:
										// - after the app shortcuts if any;
										// - visually divided from the preceeding global items or app shortcuts.
										globalCustomizeMenuItem = node;
										return null;
									}
								}
								return node;
							})}
							<TwoStageHydration
								name="invited-app-shortcuts-container"
								enabled={expVal('platform_editor_editor_ssr_streaming', 'isEnabled', false)}
							>
								{isProductionisationTreatment
									? // Productionisation cohort (PUMA-2012): render the decorated invited-app nudge ONLY when
										// GRS actually recommends an app. On an empty / cache-miss result render nothing here —
										// the Jira shortcut stays in the main GlobalAppShortcuts section (it is only handed over
										// when `shouldHandOverJiraShortcut` is true), so it is never hidden. The decorated
										// treatment (label <div> injected into the menu item's <span>, hover popup) is a
										// data-driven client enhancement whose SSR/first-client render can diverge under streaming
										// SSR (React #418/#422, PUMA-2188), so it stays inside TwoStageHydration.
										isProductionisationTreatmentActive &&
										cloudId &&
										userId && (
											<InvitedAppShortcutsContainer
												mode="productionisation"
												cloudId={cloudId}
												userId={userId}
												recommendedApps={productionisationRecommendedApps}
												sourceApp="confluence"
												shortcutsConfig={invitedAppShortcutsConfig}
												resolverResult={productionisationResolverResult}
											>
												{({ referrableShortcuts }) =>
													renderInvitedAppShortcutsMenu(referrableShortcuts)
												}
											</InvitedAppShortcutsContainer>
										)
									: // we split the kill-switch gate and targeting gate to avoid the overrides precedence issue
										// eslint-disable-next-line confluence-feature-gating/no-preconditioning
										isInvitedAppShortcutsEnabled &&
										showGlobalAppShortcuts && (
											<InvitedAppShortcutsContainer
												shortcutsConfig={invitedAppShortcutsConfig}
												resolverResult={invitedAppShortcutsExperiment}
											>
												{({ referrableShortcuts }) =>
													renderInvitedAppShortcutsMenu(referrableShortcuts)
												}
											</InvitedAppShortcutsContainer>
										)}
							</TwoStageHydration>
							{moreMenuItems.map((result) => {
								const Component = GlobalItemComponents[result.menuId];
								return (
									Component && (
										<Component
											key={result.menuId}
											isHidden
											peekingId={peekingId}
											setPeekingId={setPeekingId}
											setIsCustomizeSidebarDialogVisible={setIsCustomizeSidebarDialogVisible}
										/>
									)
								);
							})}
							{/* The app shortcuts section is after and visually divided from the global items section:  */}
							{/* DEPRECATED: App Shortcuts is now being managed by Global App Shortcuts above  */}
							{/* The item "Customize sidebar" is after and visually divided from the global items and/or app shortcuts sections: */}
							{globalCustomizeMenuItem && (
								<>
									<TopLevelSpacer />
									{globalCustomizeMenuItem}
								</>
							)}
							{error && <ErrorDisplay error={error} />}
							{isCustomizeSidebarDialogVisible && (
								<CustomizationModalLoader
									onClose={() => setIsCustomizeSidebarDialogVisible(false)}
									productItemsData={allMenuItems}
									appItemsData={appShortcutMenuItems}
									navigationCustomisationId={data?.settings_navigationCustomisation?.id || ''}
								/>
							)}
							<PageSegmentLoadEnd metric={GLOBAL_NAVIGATION_METRIC} />
						</div>
					</SpotlightTarget>
				)}
				<TopLevelSpacer />
			</MenuList>

			{productSideNavigation}

			{/* Global App Shortcuts: Platform Component for supporting Global App Shortcuts */}
			{!isOnAnySettingsScreen && showGlobalAppShortcuts && (
				<MenuList>
					<TopLevelSpacer />
					<GlobalAppShortcutsSection
						orderedMenuConfig={platformCustomizationsWithSimplifyNav}
						onUpdateGetFilteredProductLinks={(fn: fnGetFilteredProductLinks) => {
							setGetFilteredProductLinks(() => fn);

							handleUpdateInvitedAppProductLinks(fn);
						}}
						shouldHandOverJiraShortcut={shouldHandOverJiraShortcut}
					/>
					{moreMenuItem}
					<ConnectorPromoSidebarEntry />
				</MenuList>
			)}
		</>
	);
};

export const updateNavigationCustomization = (
	variables: GlobalNavigationComponentQuery$variables,
	nodes: SettingsMenuItem[],
	id: string,
): void => {
	writeQueryInterop({
		query: GlobalNavigationComponentQuery,
		variables,
		data: {
			settings_navigationCustomisation: {
				__typename: 'SettingsNavigationCustomisation',
				id,
				sidebar: {
					__typename: 'SettingsMenuItemConnection',
					nodes,
				},
			},
		},
	});
};

const GlobalNavigationComponentQuery = graphql`
	query GlobalNavigationComponentQuery($entityAri: ID!, $ownerAri: ID!) {
		settings_navigationCustomisation(entityAri: $entityAri, ownerAri: $ownerAri) {
			id
			sidebar {
				nodes {
					menuId
					visible
				}
			}
		}
	}
`;
